"use client";

import { ReactNode, useState } from "react";

import type { UploadFileType } from "@/components/utils/uploadFile";
import type { UploadLimits } from "@/components/utils/uploadBatch";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import LomdaUploadFile from "../blocks/shared/lomdaUploadFile";
import { t } from "@/lib/localization";
import { cn } from "@/lib/utils";
import {
    AttachmentBlock,
    AudioBlock,
    LomdaV3AssetType,
} from "@/lib/utils/lomdaV3/types";
import { useLomdaV3Editor } from "../../store/useLomdaV3Editor";
import {
    PopoverTitle,
    ToolbarDivider,
    WidthControl,
    chromeBtn,
    chromeBtnOpen,
    popoverShell,
} from "./blockAppearance";
import { AudioFileIcon, DocumentFileIcon } from "./mediaIcons";
import { AUDIO_UPLOAD, DOCUMENT_UPLOAD } from "../../lib/uploadLimits";

/** Topbar "Replace …" for an uploaded file: a popover with Upload / Reuse media. */
export function ReplaceMediaControl({
    icon,
    label,
    kind,
    limits,
    titleKey,
    subtitleKey,
    currentUrl,
    onUploaded,
}: {
    icon: ReactNode;
    label: string;
    kind: LomdaV3AssetType;
    limits: UploadLimits;
    titleKey: string;
    subtitleKey: string;
    currentUrl: string;
    onUploaded: (file: UploadFileType) => void;
}) {
    const [open, setOpen] = useState(false);
    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button type="button" className={cn(chromeBtn, chromeBtnOpen)}>
                    {icon}
                    {label}
                </button>
            </PopoverTrigger>
            <PopoverContent align="start" className={cn("w-[300px]", popoverShell)}>
                <div className="flex flex-col gap-4">
                    <PopoverTitle>{label}</PopoverTitle>
                    <LomdaUploadFile
                        reuse={kind}
                        selectedUrl={currentUrl}
                        options={{
                            ...limits,
                            className:
                                "flex h-40 w-full flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-border text-center",
                            title: { text: t(titleKey), keyName: titleKey },
                            subtitle: { text: t(subtitleKey), keyName: subtitleKey },
                        }}
                        onUploadFinished={(file) => {
                            onUploaded(file);
                            setOpen(false);
                        }}
                    />
                </div>
            </PopoverContent>
        </Popover>
    );
}

/** Audio topbar: Width + Replace audio (only once something is uploaded). */
export function AudioSettings({ block }: { block: AudioBlock }) {
    const updateBlock = useLomdaV3Editor((s) => s.updateBlock);
    return (
        <div className="flex items-center gap-3">
            <WidthControl blockId={block.id} />
            {block.data.url && (
                <>
                    <ToolbarDivider />
                    <ReplaceMediaControl
                        icon={<AudioFileIcon className="size-5" />}
                        label={t("lomda-v3-editor.media.replace-audio")}
                        kind="audio"
                        limits={AUDIO_UPLOAD}
                        titleKey="lomda-v3-editor.block.audio.upload-title"
                        subtitleKey="lomda-v3-editor.media.drop-hint"
                        currentUrl={block.data.url}
                        onUploaded={(file) =>
                            updateBlock(block.id, {
                                url: file.fileUrl,
                                name: file.fileName,
                            })
                        }
                    />
                </>
            )}
        </div>
    );
}

/** Document topbar: Width + Replace document. */
export function AttachmentSettings({ block }: { block: AttachmentBlock }) {
    const updateBlock = useLomdaV3Editor((s) => s.updateBlock);
    return (
        <div className="flex items-center gap-3">
            <WidthControl blockId={block.id} />
            {block.data.url && (
                <>
                    <ToolbarDivider />
                    <ReplaceMediaControl
                        icon={<DocumentFileIcon className="size-5" />}
                        label={t("lomda-v3-editor.media.replace-document")}
                        kind="file"
                        limits={DOCUMENT_UPLOAD}
                        titleKey="lomda-v3-editor.block.attachment.upload-title"
                        subtitleKey="lomda-v3-editor.block.attachment.upload-subtitle"
                        currentUrl={block.data.url}
                        onUploaded={(file) =>
                            updateBlock(block.id, {
                                url: file.fileUrl,
                                name: file.fileName,
                                size: file.fileSize ?? 0,
                            })
                        }
                    />
                </>
            )}
        </div>
    );
}
